/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import type { FormikProps } from 'formik';
import type { OptionType } from '@withjoy/joykit';
import { TextV2, FormControl, InputV2, SpacingStack, SelectV1, Flex, IconV2 } from '@withjoy/joykit';
import { ChevronDown } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import type { Contacts, ContactInputFields, FieldVisibility } from '@apps/guest/contactCollector/shared/types';
import type { IframeContacts, IframeContactInputFields } from '@apps/guest/contactCollector/shared/types';
import { useLocalizedCountryOptions } from '@shared/utils/address/useLocalizedCountryOptions';
import LegacyAddress from '@apps/card/routes/CardCustomizer/components/ContactDialog/components/LegacyAddress/LegacyAddress';

interface MailingAddressProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    index: number;
    contact: ContactInputFields | IframeContactInputFields;
    handleOnCountryDropdownChange: (option: OptionType | null, index: number) => void;
    errors: {
      address1Error: (index: number) => any;
      postalCodeError: (index: number) => any;
      cityError: (index: number) => any;
      regionError: (index: number) => any;
      countryError: (index: number) => any;
    };
    isIframeVersion?: boolean;
    mailingAddressRule?: FieldVisibility;
    isSectionOpenedByDefault?: boolean;
    isUpdateMode?: boolean;
    showEnvelopeNameOverrideField: boolean;
  }> {}

export const MailingAddress: React.FC<MailingAddressProps> = ({
  formik,
  index,
  contact,
  handleOnCountryDropdownChange,
  errors: { address1Error, postalCodeError, cityError, regionError, countryError },
  isIframeVersion = false,
  mailingAddressRule = 'optional',
  isSectionOpenedByDefault = false,
  isUpdateMode = false,
  showEnvelopeNameOverrideField,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(mailingAddressRule === 'required' || isSectionOpenedByDefault);
  const { tq } = useTranslation('contactCollector');
  const { countryOptions, localizedCountryName } = useLocalizedCountryOptions();
  const showRequiredMark = isIframeVersion && mailingAddressRule === 'required';
  const isRequired = mailingAddressRule === 'required';

  const legacyAddress = (formik.initialValues.contacts[index] as IframeContactInputFields)?.legacyAddressString;
  const showLegacyAddressStringField =
    isUpdateMode &&
    !!legacyAddress &&
    (!formik.initialValues.contacts[index]?.address1 || !formik.initialValues.contacts[index]?.city || !formik.initialValues.contacts[index]?.postalCode);

  return (
    <>
      <Flex
        gap={2}
        alignItems="center"
        marginTop="2rem !important"
        onClick={() => setIsOpen(prevState => !prevState)}
        cursor="pointer"
        marginBottom="12px"
        justifyContent="space-between"
      >
        <TextV2
          typographyVariant="hed4"
          width="100%"
        >
          {tq`Mailing Address`}
        </TextV2>
        {isIframeVersion && (
          <IconV2
            size="md"
            style={{ transform: isOpen ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 500ms cubic-bezier(0.23,1,0.32,1) 0s' }}
          >
            <ChevronDown />
          </IconV2>
        )}
      </Flex>
      {showLegacyAddressStringField && <LegacyAddress value={(formik.values.contacts[index] as IframeContactInputFields)?.legacyAddressString} />}
      {(isOpen || !isIframeVersion) && (
        <>
          {showEnvelopeNameOverrideField && (
            <FormControl label={tq`Envelope Name`}>
              <InputV2
                {...formik.getFieldProps(`contacts[${index}].envelopeNameOverride`)}
                placeholder={tq`The Smith Family, Mr. and Mrs. Smith, etc`}
              />
            </FormControl>
          )}
          <FormControl
            label={showRequiredMark ? tq`Address Line 1*` : tq`Address Line 1`}
            isInvalid={!!address1Error(index)}
            error={address1Error(index)}
            isRequired={isRequired}
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].address1`)}
              autoComplete="address-line1"
            />
          </FormControl>
          <FormControl label={tq`Apartment, Suite, etc. (Optional)`}>
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].address2`)}
              autoComplete="address-line2"
            />
          </FormControl>
          <FormControl
            label={showRequiredMark ? tq`City*` : tq`City`}
            isInvalid={!!cityError(index)}
            error={cityError(index)}
            isRequired={isRequired}
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].city`)}
              autoComplete="address-level2"
            />
          </FormControl>
          <FormControl
            label={showRequiredMark ? tq`State/Province/Territory*` : tq`State/Province/Territory`}
            isInvalid={!!regionError(index)}
            error={regionError(index)}
            isRequired={isRequired}
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].region`)}
              autoComplete="address-level1"
            />
          </FormControl>
          <FormControl
            label={showRequiredMark ? tq`Zip/Postal Code*` : tq`Zip/Postal Code`}
            isInvalid={!!postalCodeError(index)}
            error={postalCodeError(index)}
            isRequired={isRequired}
          >
            <InputV2
              {...formik.getFieldProps(`contacts[${index}].postalCode`)}
              autoComplete="postal-code"
            />
          </FormControl>
          <FormControl
            label={tq`Country/Region`}
            isInvalid={!!countryError(index)}
            error={countryError(index)}
          >
            <SpacingStack spacing={3}>
              <SelectV1
                options={countryOptions}
                value={{ label: localizedCountryName(contact.countryCode) ?? contact.country, value: contact.countryCode } as OptionType}
                searchable={false}
                onChange={value => handleOnCountryDropdownChange(value, index)}
              />
            </SpacingStack>
          </FormControl>
        </>
      )}
    </>
  );
};
